import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  fetchTaskStatus,
  getAllDocuments,
  syncRules,
  updateDocumentNameApi,
} from "../../../../Services/DocumentUpload/documentUploadApi";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DatePicker from "react-datepicker";
import { resetTaskStatus } from "../../../../Services/DocumentUpload/documentSlice/fetchTaskStatusSlice";

const EditDocument = (props) => {
  const [fileName, setFileName] = useState(props.fileNameToEdit);
  const [versionDate, setVersionDate] = useState(props.versionDateToEdit || "");
  const [selectedDate, setSelectedDate] = useState(null);
  const [documentId, setDocumentId] = useState(props.documentIdForEdit);
  const [dateError, setDateError] = useState("");
  const [nameError, setNameError] = useState("");
  const userId = localStorage.getItem("user_id");
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialFileName, setInitialFileName] = useState(props.fileNameToEdit);
  const [initialVersionDate, setInitialVersionDate] = useState(
    props.versionDateToEdit || ""
  );
  const [taskId, setTaskId] = useState(null);
  

    useEffect(() => {
      if (!taskId) return;
  
      setLoading(true); // Set loading to true when task starts
  
      const intervalId = setInterval(() => {
        dispatch(fetchTaskStatus(taskId)).then((response) => {
          const status = response?.payload?.data?.status;
          if (status === "completed") {
            toast.success(response?.payload?.message);
            clearInterval(intervalId);
            dispatch(resetTaskStatus());
            setTaskId(null);
            setLoading(false); // Set loading to false when task completes
            props.refetch();
          }
        });
      }, 3000);
  
      return () => {
        clearInterval(intervalId);
        setLoading(false); // Ensure loading is set to false on cleanup
      };
    }, [dispatch, taskId]);

  useEffect(() => {
    if (props.fileNameToEdit) {
      setFileName(props.fileNameToEdit);
    }
    if (props.documentIdForEdit) {
      setDocumentId(props.documentIdForEdit);
    }
  }, [props]);

  useEffect(() => {
    if (props.versionDateToEdit) {
      setVersionDate(props.versionDateToEdit);

      try {
        // Split by "/" since format is MM/DD/YYYY
        const [month, day, year] = props.versionDateToEdit.split("/");

        const date = new Date(year, parseInt(month) - 1, parseInt(day));

        if (date instanceof Date && !isNaN(date)) {
          setSelectedDate(date);
        } else {
          setSelectedDate(null);
        }
      } catch (error) {
        console.error("Error parsing date:", error);
        setSelectedDate(null);
      }
    }
  }, [props.versionDateToEdit]);

  useEffect(() => {
    setInitialFileName(props.fileNameToEdit);
    setInitialVersionDate(props.versionDateToEdit || "");
  }, [props.fileNameToEdit, props.versionDateToEdit]);

  const handleFileNameChange = (e) => {
    const newFileName = e.target.value;
    setFileName(newFileName);
    validateFileName(newFileName);
  };

  const validateDate = (dateString) => {
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!regex.test(dateString)) {
      setDateError("Please enter date in MM/DD/YYYY format");
      return false;
    }
    setDateError("");
    return true;
  };

  const validateFileName = (name) => {
    if (!name || name.trim() === "") {
      setNameError("File name cannot be empty");
      return false;
    }
    setNameError("");
    return true;
  };

  const handleDateChange = (e) => {
    const input = e.target.value;
    setVersionDate(input);
    if (input) validateDate(input);
  };

  const handleDatePickerChange = (date) => {
    if (date) {
      const formattedDate = `${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}/${String(date.getDate()).padStart(2, "0")}/${date.getFullYear()}`;
      setSelectedDate(date);
      setVersionDate(formattedDate);
      validateDate(formattedDate);
    }
  };

  const hasChanges = () => {
    return fileName !== initialFileName || versionDate !== initialVersionDate;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateFileName(fileName) ||
      (versionDate && !validateDate(versionDate))
    ) {
      return;
    }

    setLoading(true);

    const [month, day, year] = versionDate.split("/");
    const apiDate = `${day}-${month}-${year}`;

    const documents = [
      {
        id: documentId,
        name: fileName,
        version_date: versionDate ? apiDate : null,
      },
    ];

    try {
      await dispatch(
        syncRules({
          id: documentId,
          name: fileName !==props.fileNameToEdit ? fileName : null,
          community_id:props.community_id_for_doc,
          version_date: versionDate !== props.versionDateToEdit ? apiDate : null,
        })
      )
        .unwrap()
        .then((res) => {
          toast.success(res?.message);
          props.refetch();
          setTaskId(res.data.task_id);
          dispatch(
            getAllDocuments({
              community_id: props.community_id_for_doc,
              page: props.page,
              page_size: props.pageSize,
              search: props.search,
              access: "all",
              user_id: userId,
            })
          ).unwrap();
        })
        .finally(() => {
          setLoading(false);
          props.onHide();
        });
    } catch (error) {
      console.error("Document edit error:", error);
      toast.error(error.response?.data?.message || "Failed to edit document");
      setLoading(false);
    }
  };

  return (
    <Modal
      {...props}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Edit File Name
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* <h4>Enter the file name</h4> */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="fileName">File Name:</label>
            <input
              type="text"
              id="fileName"
              className={`form-control mb-2 ${nameError ? "is-invalid" : ""}`}
              value={fileName}
              onChange={handleFileNameChange}
              required
            />
            {nameError && (
              <div className="invalid-feedback d-block">{nameError}</div>
            )}
          </div>

          <label htmlFor="versionDate">Version Date (MM/DD/YYYY):</label>
          <div className="input-group">
            <input
              type="text"
              id="versionDate"
              className={`form-control ${dateError ? "is-invalid" : ""}`}
              value={versionDate}
              onChange={handleDateChange}
              placeholder="MM/DD/YYYY"
              required
            />
            <div className="input-group-append">
              <DatePicker
                selected={selectedDate}
                onChange={handleDatePickerChange}
                dateFormat="MM/dd/yyyy"
                customInput={
                  <button className="btn btn-outline-secondary" type="button">
                    <CalendarMonthOutlinedIcon />
                  </button>
                }
                maxDate={new Date()}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>
          {dateError && (
            <div className="invalid-feedback d-block">{dateError}</div>
          )}
          <small className="form-text text-muted">
            Enter date in MM/DD/YYYY format or select from calendar
          </small>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button onClick={props.onHide} className="bttn-close">
          Close
        </button>
        <button
          onClick={handleSubmit}
          className="bttn-upload"
          disabled={loading || !hasChanges() || dateError}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditDocument;
